import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PaymentMethod } from '../../../../../core/enums/commerce/payment/payment-method';
import { nameOf } from '../../../../../core/functions/string-helpers';
import { CreditCardSummary } from '../../../../../core/models/commerce/cards-on-file/credit-card-summary';
import { FormGroupType } from '../../../../../core/models/form-group-type';
import { CreditCardSummaryService } from '../../../../../core/services/commerce/credit-card-summary/credit-card-summary.service';
import { PaymentMethodValidatorMap } from '../../shared/constants/payment-method-validator-map';
import { PaymentValidationMessages } from '../../shared/constants/payment-validation-messages';
import { PaymentSourceFormService } from '../../shared/services/payment-source-form/payment-source-form.service';
import {
    maxAmountValidator,
    notExpiredValidator,
} from '../../shared/validators/order-payment-validators';
import { PaymentTransactionConstants } from '../constants/payment-transaction-constants';
import { PaymentTransactionFormModel } from '../models/payment-transaction-form-model';

@Injectable({
    providedIn: 'root',
})
export class PaymentTransactionFormService extends PaymentSourceFormService<PaymentTransactionFormModel> {
    private readonly creditCardSummaryService = inject(
        CreditCardSummaryService,
    );

    getFormDefinition(
        remainingBalance: number,
    ): FormGroup<FormGroupType<PaymentTransactionFormModel>> {
        return new FormGroup<FormGroupType<PaymentTransactionFormModel>>({
            paymentMethod: new FormControl(
                PaymentMethod.AuthNetAim,
                Validators.required,
            ),
            cardOnFileId: new FormControl<number | null>(null),
            amount: new FormControl<number>(0, {
                nonNullable: true,
                validators: [
                    Validators.required,
                    Validators.min(
                        PaymentTransactionConstants.MIN_PAYMENT_AMOUNT,
                    ),
                    maxAmountValidator(remainingBalance, {
                        label: nameOf<typeof PaymentValidationMessages>(
                            'amountExceedsBalance',
                        ),
                        message: PaymentValidationMessages.amountExceedsBalance,
                    }),
                ],
            }),
            sendInvoiceUponSave: new FormControl<boolean>(false),
        });
    }

    /**
     * Refreshes the amount form control's value and validators based on the remaining balance for the order.
     *
     * @param form The form group containing the amount control.
     * @param remainingBalance The remaining balance for the order.
     */
    refreshAmountFormControlValueAndValidators(
        form: FormGroup<FormGroupType<PaymentTransactionFormModel>>,
        remainingBalance: number,
    ): void {
        form.controls.amount.setValidators([
            Validators.required,
            Validators.min(PaymentTransactionConstants.MIN_PAYMENT_AMOUNT),
            maxAmountValidator(remainingBalance, {
                label: nameOf<typeof PaymentValidationMessages>(
                    'amountExceedsBalance',
                ),
                message: PaymentValidationMessages.amountExceedsBalance,
            }),
        ]);

        form.controls.amount.patchValue(remainingBalance, { emitEvent: false });
    }

    /**
     * Refreshes the validators on the form controls based on the currently selected payment method.
     */
    protected getPaymentMethodValidatorMap(
        getCreditCards: () => CreditCardSummary[],
    ): PaymentMethodValidatorMap<PaymentTransactionFormModel> {
        return {
            [PaymentMethod.AuthNetAim]: {
                cardOnFileId: [
                    Validators.required,
                    notExpiredValidator(
                        getCreditCards,
                        this.creditCardSummaryService.isExpired,
                    ),
                ],
                sendInvoiceUponSave: [],
            },
            [PaymentMethod.InvoiceNet30]: {
                cardOnFileId: [],
                sendInvoiceUponSave: [Validators.required],
            },
        };
    }
}
